<template>
  <div>
    <v-row dense class="mb-3">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="filters.search"
          :label="$t('buscar')"
          clearable
          dense
          outlined
          prepend-inner-icon="mdi-magnify"
        />
      </v-col>

      <v-col cols="12" md="4">
        <v-select
          v-model="filters.invoiceStatus"
          :items="invoiceStatusOptions"
          :label="$t('estado_factura')"
          clearable
          dense
          outlined
        />
      </v-col>

      <v-col cols="12" md="4">
        <v-select
          v-model="filters.uploadStatus"
          :items="uploadStatusOptions"
          :label="$t('cargar_factura')"
          clearable
          dense
          outlined
        />
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredOrdenes"
      :items-per-page="10"
      :footer-props="footerProps"
      :loading="loadingOrdenes"
      :no-data-text="$t('sin_resultados')"
      item-key="id"
    >
      <template v-slot:item.folio="{ item }">
        {{ getFolio(item) }}
      </template>

      <template v-slot:item.fecha="{ item }">
        {{ formatDateTime(item.fecha) }}
      </template>

      <template v-slot:item.estadoFactura="{ item }">
        {{ getInvoiceStatus(item) }}
      </template>

      <template v-slot:item.total="{ item }">
        ${{ formatNumber(getTotal(item)) }}
      </template>

      <template v-slot:item.opciones="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="canUploadInvoice(item)"
              color="primary"
              fab
              x-small
              dark
              v-on="on"
              @click="cargarFactura(item)"
            >
              <v-icon>mdi-upload</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("cargar_factura") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <CargarFactura
      v-if="modalCargaFactura"
      :orden="orden"
      :modal="modalCargaFactura"
      @close="cerrarCargaFactura"
    />
  </div>
</template>

<script>
import CargarFactura from "./CargarFactura";

export default {
  name: "OrdenCompra",
  components: {
    CargarFactura,
  },
  data: () => ({
    ordenes: [],
    orden: {},
    modalCargaFactura: false,
    generalConfig: {},
    loadingOrdenes: false,
    filters: {
      search: "",
      invoiceStatus: "",
      uploadStatus: "",
    },
  }),
  computed: {
    headers() {
      return [
        { text: "Folio", value: "folio" },
        { text: this.$t("fecha"), value: "fecha" },
        { text: this.$t("estado_factura"), value: "estadoFactura" },
        { text: "Total", value: "total" },
        {
          text: this.$t("opciones"),
          value: "opciones",
          sortable: false,
          align: "center",
        },
      ];
    },
    footerProps() {
      return {
        "items-per-page-options": [10, 25, 50, -1],
        "items-per-page-text": this.$t("registros_por_pagina"),
      };
    },
    invoiceStatusOptions() {
      const statuses = this.ordenes.map((orden) => this.getInvoiceStatus(orden));
      return [...new Set(statuses)].sort();
    },
    uploadStatusOptions() {
      return [
        { text: this.$t("con_carga_disponible"), value: "available" },
        { text: this.$t("sin_carga_disponible"), value: "unavailable" },
      ];
    },
    filteredOrdenes() {
      return this.ordenes.filter((orden) => {
        const search = (this.filters.search || "").toLowerCase().trim();
        const searchableText = [
          this.getFolio(orden),
          this.getInvoiceStatus(orden),
          this.formatDateTime(orden.fecha),
          this.formatNumber(this.getTotal(orden)),
        ].join(" ").toLowerCase();

        const matchesSearch = !search || searchableText.includes(search);
        const matchesInvoiceStatus =
          !this.filters.invoiceStatus ||
          this.getInvoiceStatus(orden) === this.filters.invoiceStatus;
        const matchesUploadStatus =
          !this.filters.uploadStatus ||
          (this.filters.uploadStatus === "available" && this.canUploadInvoice(orden)) ||
          (this.filters.uploadStatus === "unavailable" && !this.canUploadInvoice(orden));

        return matchesSearch && matchesInvoiceStatus && matchesUploadStatus;
      });
    },
  },
  methods: {
    async getOrdenes() {
      this.loadingOrdenes = true;
      this.openLoader();
      const ordenesURL = `${this.$store.state.proveedorUrl}compras/ordenes`;
      const ordenesParams = {
        proveedor_id: this.$store.state.user.id,
        estado: "Finalizada",
        externo: 1,
      };

      const { data, success } = await this.sendRequest(ordenesURL, ordenesParams);
      if (success) this.ordenes = data;

      this.loadingOrdenes = false;
      this.closeLoader();
    },
    getGeneralConfig: async function () {
      try {
        const response = await this.sendRequest(`${this.$store.state.proveedorUrl}configuracion/general`, {});
        return response.data;
      } catch (error) {
        console.error('Error fetching general configuration:', error);
        return {};
      }
    },
    cargarFactura(orden) {
      this.orden = orden;
      this.modalCargaFactura = true;
    },
    cerrarCargaFactura() {
      this.modalCargaFactura = false;
      this.getOrdenes();
    },
    getFolio(orden) {
      return String(orden.folio || "").replace("amp;", "");
    },
    getInvoiceStatus(orden) {
      const facturas = Array.isArray(orden.facturas) ? orden.facturas : [];
      return facturas.length >= 1 ? facturas[0].estado : this.$t("nd");
    },
    getTotal(orden) {
      return orden[`total${orden.internacional}`];
    },
    canUploadInvoice(orden) {
      const facturas = Array.isArray(orden.facturas) ? orden.facturas : [];
      const docsRepse = Array.isArray(orden.docs_repse) ? orden.docs_repse : [];
      const hasPendingRepseDocs = docsRepse.some((doc) =>
        ["No Validado", "Pendiente"].includes(doc.estado)
      );

      return Boolean(
        this.generalConfig.allow_external_invoice_upload &&
        (facturas.length === 0 || hasPendingRepseDocs)
      );
    },
  },
  async created() {
    if (!this.$store.state.user.id) {
      this.clearSession();
      this.redirectToLogin();
      return;
    }
    const generalConfig = await this.getGeneralConfig();
    this.generalConfig = generalConfig;
    localStorage.setItem('generalConfig', JSON.stringify(generalConfig));
    await this.getOrdenes();
  },
};
</script>
